import React, { useState, useEffect } from "react";
import InvoiceNumberingSettings from "../components/invoices/InvoiceNumberingSettings";
import {
  ACTIVITY_PROFILE_OPTIONS,
  getCompanyProfiles,
  getActivityProfileLabels,
  getSelectionDomain,
} from "../utils/activityProfiles";
import {
  Table,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Upload,
  notification,
  Spin,
  Space,
  Row,
  Col,
  message,
  Card,
  Typography,
  Avatar,
  Tag,
  Tabs,
  Tooltip,
  Divider,
  Badge,
  Empty,
  Descriptions,
  Pagination,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  UserOutlined,
  SearchOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  BankOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  SettingOutlined,
  CalendarOutlined,
  PictureOutlined,
  SafetyCertificateOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import CompanyLegislationManager from "../gestion-rh/tax-legislation/CompanyLegislationManager.tsx";
import AdminPortfolioExportButtons from "../components/AdminPortfolioExportButtons";
import API_CONFIG from '../config/api.js';
import "./GestionCompanies.css";

// Configuration d'axios (endpoint API)
axios.defaults.baseURL = API_CONFIG.BASE_URL;

// Configuration globale des notifications
notification.config({
  duration: 3,
  placement: "topRight",
  top: 80,
  style: { fontSize: "14px", borderRadius: "8px" },
});

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const COMPANY_EDIT_SETTINGS_PERMISSION =
  "Admin.GestionEntreprises.edit_settings";
// Source unique (miroir backend COMPANY_ACTIVITY_PROFILES), importee depuis
// utils/activityProfiles. Chaque option porte aussi son `domain` (utilise pour
// la contrainte de cumul INTRA-domaine du multi-select).
const activityProfileOptions = ACTIVITY_PROFILE_OPTIONS;

const activityProfileLabelMap = activityProfileOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const defaultCurrencyOptions = [
  { value: "XOF", label: "Franc CFA BCEAO (XOF)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "USD", label: "Dollar US (USD)" },
];

const defaultCurrencyLabelMap = defaultCurrencyOptions.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const getCompanyCurrencyValue = (company) => {
  if (!company) return "XOF";
  if (company.currency_code) return company.currency_code;
  if (typeof company.currency_id === "string" && Number.isNaN(Number(company.currency_id))) {
    return company.currency_id;
  }
  return "XOF";
};

const formatCurrencyOptionLabel = (currency) => {
  const symbolSuffix = currency.symbol ? ` - ${currency.symbol}` : "";
  return `${currency.name} (${currency.code})${symbolSuffix}`;
};

const getFallbackCurrencyOptions = (company) => {
  if (!company) {
    return defaultCurrencyOptions;
  }

  const currencyValue = getCompanyCurrencyValue(company);
  const fallbackLabel =
    company.currency_name && company.currency_code
      ? `${company.currency_name} (${company.currency_code})`
      : defaultCurrencyLabelMap[currencyValue] || currencyValue;

  return [{ value: currencyValue, label: fallbackLabel }];
};

const buildCompanyFormValues = (company) => ({
  name: company.name,
  shortName: company.short_name,
  email: company.email,
  phone: company.phone,
  website: company.website,
  address: company.address,
  currency: getCompanyCurrencyValue(company),
  activityProfiles: getCompanyProfiles(company),
  isActive: company.status === "active",
});

const isEnabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const displayValue = (value, fallback = "Non renseigné") =>
  value === null || value === undefined || value === "" ? fallback : value;

const formatDate = (value, includeTime = false) => {
  if (!value) return "Non renseignée";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
};

const getCompanyLogoItems = (company) => [
  { key: "light_logo", label: "Logo clair", url: company?.light_logo },
  {
    key: "dark_logo",
    label: "Logo sombre",
    url: company?.dark_logo,
    dark: true,
  },
  {
    key: "small_light_logo",
    label: "Petit logo clair",
    url: company?.small_light_logo,
  },
  {
    key: "small_dark_logo",
    label: "Petit logo sombre",
    url: company?.small_dark_logo,
    dark: true,
  },
  {
    key: "login_image",
    label: "Image de connexion",
    url: company?.login_image,
  },
];

const GestionCompanies = () => {
  const { user, token, hasPermission } = useAuth();
  const isSuperAdmin = Boolean(user?.is_superadmin);
  const canEditCompanySettings =
    isSuperAdmin || hasPermission(COMPANY_EDIT_SETTINGS_PERMISSION);

  // États principaux
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailCompany, setDetailCompany] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [form] = Form.useForm();
  const [currencyOptions, setCurrencyOptions] = useState(defaultCurrencyOptions);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768); // État pour la taille de l'écran

  // Configuration d'axios avec le token d'authentification
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  // Effet pour mettre à jour isSmallScreen lors du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Chargement des entreprises depuis l'API avec filtre sur le statut
  const fetchCompanies = async (page = 1) => {
    try {
      setLoading(true);
      const statusFilter = showInactive ? "inactive" : "active";
      const response = await axios.get(
        `/api/companies?page=${page}&status=${statusFilter}`
      );
      setCompanies(response.data.companies);
      setTotalItems(response.data.pagination.totalItems);
      setCurrentPage(page);
    } catch (error) {
      notification.error({
        message: "Erreur de chargement",
        description:
          error.response?.data?.error ||
          "Erreur lors du chargement des entreprises",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies(currentPage);
  }, [currentPage, showInactive]);

  // Effet pour filtrer les entreprises localement lorsque le texte de recherche change
  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredCompanies(companies);
    } else {
      const searchTermLower = searchText.toLowerCase();
      const filtered = companies.filter(
        (company) =>
          String(company.name || "")
            .toLowerCase()
            .includes(searchTermLower) ||
          (company.short_name &&
            company.short_name.toLowerCase().includes(searchTermLower)) ||
          String(company.email || "")
            .toLowerCase()
            .includes(searchTermLower) ||
          String(company.phone || "")
            .toLowerCase()
            .includes(searchTermLower) ||
          (company.website &&
            company.website.toLowerCase().includes(searchTermLower))
      );
      setFilteredCompanies(filtered);
    }
  }, [searchText, companies]);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyCurrencies = async () => {
      if (!modalVisible) {
        return;
      }

      if (!selectedCompany?.id) {
        setCurrencyOptions(defaultCurrencyOptions);
        setCurrenciesLoading(false);
        return;
      }

      setCurrenciesLoading(true);

      try {
        const response = await axios.get(
          `/api/currencies?companyId=${selectedCompany.id}&limit=1000`
        );
        const fetchedCurrencies = Array.isArray(response.data?.currencies)
          ? response.data.currencies
          : [];

        if (!isMounted) {
          return;
        }

        if (fetchedCurrencies.length > 0) {
          const nextOptions = fetchedCurrencies.map((currency) => ({
            value: currency.code,
            label: formatCurrencyOptionLabel(currency),
          }));
          const currentValue = getCompanyCurrencyValue(selectedCompany);

          if (!nextOptions.some((option) => option.value === currentValue)) {
            nextOptions.unshift(...getFallbackCurrencyOptions(selectedCompany));
          }

          setCurrencyOptions(nextOptions);
        } else {
          setCurrencyOptions(getFallbackCurrencyOptions(selectedCompany));
        }
      } catch (error) {
        if (isMounted) {
          setCurrencyOptions(getFallbackCurrencyOptions(selectedCompany));
        }
      } finally {
        if (isMounted) {
          setCurrenciesLoading(false);
        }
      }
    };

    fetchCompanyCurrencies();

    return () => {
      isMounted = false;
    };
  }, [modalVisible, selectedCompany]);

  const openCreateModal = () => {
    setSelectedCompany(null);
    setCurrencyOptions(defaultCurrencyOptions);
    form.resetFields();
    form.setFieldsValue({ currency: "XOF", isActive: true });
    setModalVisible(true);
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setCurrencyOptions(getFallbackCurrencyOptions(company));
    form.setFieldsValue(buildCompanyFormValues(company));
    setModalVisible(true);
  };

  // Gestion de la soumission du formulaire (création ou modification)
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("short_name", values.shortName || "");
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      formData.append("website", values.website || "");
      formData.append("address", values.address || "");
      if (isSuperAdmin) {
        formData.append("status", values.isActive ? "active" : "inactive");
      }
      formData.append("currency_id", values.currency);
      if (isSuperAdmin) {
        formData.append(
          "activity_profiles",
          JSON.stringify(
            Array.isArray(values.activityProfiles) ? values.activityProfiles : []
          )
        );
      }

      // Traitement des logos
      const logoFields = [
        { formKey: "lightLogo", dbKey: "light_logo" },
        { formKey: "darkLogo", dbKey: "dark_logo" },
        { formKey: "smallLightLogo", dbKey: "small_light_logo" },
        { formKey: "smallDarkLogo", dbKey: "small_dark_logo" },
      ];
      logoFields.forEach(({ formKey, dbKey }) => {
        if (values[formKey] && values[formKey].length > 0) {
          formData.append(dbKey, values[formKey][0].originFileObj);
        }
      });

      setLoading(true);
      if (selectedCompany) {
        await axios.put(`/api/companies/${selectedCompany.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success({
          content: "Entreprise mise à jour avec succès",
          icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        });
      } else {
        await axios.post(`/api/companies`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success({
          content: "Entreprise créée avec succès",
          icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        });
      }
      fetchCompanies(currentPage);
      setModalVisible(false);
      form.resetFields();
      setSelectedCompany(null);
      setCurrencyOptions(defaultCurrencyOptions);
      setCurrenciesLoading(false);
    } catch (error) {
      notification.error({
        message: "Erreur",
        description: error.response?.data?.error || "Une erreur s'est produite",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    } finally {
      setLoading(false);
    }
  };

  // Suppression d'une entreprise avec confirmation
  const handleDelete = (company) => {
    Modal.confirm({
      title: "Confirmer la suppression",
      content: "Êtes-vous sûr de vouloir supprimer cette entreprise ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      onOk: async () => {
        try {
          setLoading(true);
          await axios.delete(`/api/companies/${company.id}`);
          message.success({
            content: "Entreprise supprimée avec succès",
            icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
          });
          if (companies.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            fetchCompanies(currentPage);
          }
        } catch (error) {
          notification.error({
            message: "Erreur",
            description:
              error.response?.data?.error ||
              "Erreur lors de la suppression de l'entreprise",
            icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Basculement du statut d'une entreprise
  const handleToggleStatus = async (company) => {
    if (!isSuperAdmin) return;
    const newStatus = company.status === "active" ? "inactive" : "active";
    try {
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, status: newStatus } : c))
      );
      await axios.patch(`/api/companies/${company.id}/status`, {
        status: newStatus,
      });
      message.success({
        content: "Statut mis à jour avec succès",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      });
    } catch (error) {
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, status: company.status } : c
        )
      );
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          "Erreur lors de la mise à jour du statut",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    }
  };

  const showCompanyDetails = async (company) => {
    setDetailCompany(company);
    setDetailVisible(true);
    setDetailLoading(true);

    try {
      const response = await axios.get(`/api/companies/${company.id}`);
      setDetailCompany(response.data);
    } catch (error) {
      notification.warning({
        message: "Détails partiels",
        description:
          "Les informations actualisées n'ont pas pu être chargées. Les données de la liste sont affichées.",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  // Vérification de l'authentification
  if (!user) {
    return (
      <div
        style={{
          padding: "24px",
          background: "var(--bg-secondary)",
          minHeight: "100vh",
          transition: "all 0.3s ease",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Card style={{ textAlign: "center", maxWidth: 400 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Vérification de l'authentification...</Text>
          </div>
        </Card>
      </div>
    );
  }

  // Définition des colonnes du tableau
  const columns = [
    {
      title: "Entreprise",
      key: "company",
      render: (_, record) => (
        <Space>
          <Avatar
            size={40}
            icon={<UserOutlined />}
            src={record.light_logo}
            style={{ backgroundColor: "var(--color-primary)" }}
          />
          <div>
            <Text strong>{record.name}</Text>
            {record.short_name && (
              <div>
                <Text type="secondary">{record.short_name}</Text>
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      responsive: ["sm"],
      render: (text) => (
        <Space>
          <MailOutlined style={{ color: "var(--color-primary)" }} />
          <Text copyable>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      responsive: ["sm"],
      render: (text) => (
        <Space>
          <PhoneOutlined style={{ color: "var(--color-primary)" }} />
          <Text copyable>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Site Web",
      dataIndex: "website",
      key: "website",
      responsive: ["md"],
      render: (text) => {
        if (!text) return <Text type="secondary">-</Text>;
        return (
          <Space>
            <GlobalOutlined style={{ color: "var(--color-primary)" }} />
            <a href={text} target="_blank" rel="noopener noreferrer">
              {text.replace(/^https?:\/\//, "")}
            </a>
          </Space>
        );
      },
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text) => (
        <Tag
          color={text === "active" ? "success" : "error"}
          style={{ borderRadius: "12px", padding: "4px 12px" }}
        >
          {text === "active" ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showCompanyDetails(record)}
            />
          </Tooltip>
          {canEditCompanySettings ? (
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          ) : null}
          {isSuperAdmin ? (
            <Tooltip title="Supprimer">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          ) : null}
          {isSuperAdmin ? (
            <Tooltip
              title={record.status === "active" ? "Désactiver" : "Activer"}
            >
              <Switch
                size="small"
                checked={record.status === "active"}
                onChange={() => handleToggleStatus(record)}
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  // Rendu d'une entreprise en mode carte
  const renderCompanyCard = (company) => {
    return (
      <Col xs={24} sm={12} md={8} lg={6} xl={4} key={company.id}>
        <Card
          hoverable
          style={{
            marginBottom: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
          actions={[
            <Tooltip title="Voir détails" key="view">
              <EyeOutlined onClick={() => showCompanyDetails(company)} />
            </Tooltip>,
            ...(canEditCompanySettings
              ? [
                  <Tooltip title="Modifier" key="edit">
                    <EditOutlined
                      onClick={() => openEditModal(company)}
                    />
                  </Tooltip>,
                ]
              : []),
            ...(isSuperAdmin
              ? [
                  <Tooltip title="Supprimer" key="delete">
                    <DeleteOutlined onClick={() => handleDelete(company)} />
                  </Tooltip>,
                ]
              : []),
          ]}
        >
          <div
            style={{
              position: "relative",
              textAlign: "center",
              marginBottom: 16,
            }}
          >
            <Avatar
              size={64}
              icon={<UserOutlined />}
              src={company.light_logo}
              style={{ backgroundColor: "var(--color-primary)" }}
            />
            <Badge
              status={company.status === "active" ? "success" : "error"}
              style={{ position: "absolute", top: 8, right: 8 }}
            />
          </div>
          <div style={{ textAlign: "center" }}>
            <Title level={5} ellipsis style={{ marginBottom: 4 }}>
              {company.name}
            </Title>
            {company.short_name && (
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 12 }}
              >
                {company.short_name}
              </Text>
            )}
          </div>
          <Divider style={{ margin: "12px 0" }} />
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div>
              <MailOutlined style={{ marginRight: 8, color: "var(--color-primary)" }} />
              <Text ellipsis style={{ maxWidth: "calc(100% - 24px)" }}>
                {company.email}
              </Text>
            </div>
            <div>
              <PhoneOutlined style={{ marginRight: 8, color: "var(--color-primary)" }} />
              <Text>{company.phone}</Text>
            </div>
            {company.website && (
              <div>
                <GlobalOutlined style={{ marginRight: 8, color: "var(--color-primary)" }} />
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {company.website.replace(/^https?:\/\//, "")}
                </a>
              </div>
            )}
          </Space>
        </Card>
      </Col>
    );
  };

  const displayedCompanies =
    searchText.trim() !== "" ? filteredCompanies : companies;

  return (
    <div className="gestion-companies-container">
      <div className="companies-header-section">
        <div className="companies-button-group">
          {isSuperAdmin ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              Nouvelle entreprise
            </Button>
          ) : (
            <div className="companies-access-note">
              <InfoCircleOutlined />
              <span>Entreprises auxquelles vous avez accès</span>
            </div>
          )}
          {isSuperAdmin && <AdminPortfolioExportButtons kind="companies" />}
        </div>

        <div className="companies-filter-section">
          <Input
            placeholder="Rechercher une entreprise..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            allowClear
            className="companies-search"
          />
          <Select
            value={showInactive ? "inactive" : "active"}
            onChange={(value) => {
              setCurrentPage(1);
              setShowInactive(value === "inactive");
            }}
            className="companies-status-filter"
            options={[
              { value: "active", label: "Entreprises actives" },
              { value: "inactive", label: "Entreprises inactives" },
            ]}
          />
          <Space.Compact className="companies-view-switcher">
            <Tooltip title="Vue liste">
              <Button
                aria-label="Vue liste"
                icon={<UnorderedListOutlined />}
                type={viewMode === "table" ? "primary" : "default"}
                onClick={() => setViewMode("table")}
              />
            </Tooltip>
            <Tooltip title="Vue cartes">
              <Button
                aria-label="Vue cartes"
                icon={<AppstoreOutlined />}
                type={viewMode === "card" ? "primary" : "default"}
                onClick={() => setViewMode("card")}
              />
            </Tooltip>
          </Space.Compact>
          <Tooltip title="Actualiser">
            <Button
              aria-label="Actualiser la liste"
              icon={<ReloadOutlined />}
              onClick={() => fetchCompanies(currentPage)}
            />
          </Tooltip>
        </div>
      </div>

      {viewMode === "table" ? (
        <Table
          dataSource={displayedCompanies}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={false}
          bordered={false}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description="Aucune entreprise trouvée"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      ) : loading ? (
        <div className="companies-loading-state">
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {displayedCompanies.length > 0 ? (
            displayedCompanies.map(renderCompanyCard)
          ) : (
            <Col span={24} className="companies-empty-state">
              <Empty
                description="Aucune entreprise trouvée"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Col>
          )}
        </Row>
      )}

      <div className="companies-pagination-container">
        {searchText.trim() ? (
          <Text type="secondary">
            {displayedCompanies.length} résultat
            {displayedCompanies.length > 1 ? "s" : ""} sur cette page
          </Text>
        ) : (
          <Pagination
            current={currentPage}
            pageSize={10}
            total={totalItems}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} sur ${total} entreprises`
            }
          />
        )}
      </div>

      <Modal
        visible={modalVisible}
        title={
          <Title level={4}>
            {selectedCompany ? "Modifier l'entreprise" : "Créer une entreprise"}
          </Title>
        }
        width={isSmallScreen ? "95%" : 1000} // Largeur adaptative
        style={{ top: 20 }}
        bodyStyle={{ padding: "24px" }}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedCompany(null);
          setCurrencyOptions(defaultCurrencyOptions);
          setCurrenciesLoading(false);
        }}
        onOk={handleFormSubmit}
        okText={selectedCompany ? "Mettre à jour" : "Créer"}
        destroyOnClose
        centered
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ currency: "XOF", isActive: true }}
        >
          <Tabs type="card">
            <TabPane tab="Informations générales" key="1">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Nom ou raison sociale"
                    name="name"
                    rules={[{ required: true, message: "Le nom est requis" }]}
                  >
                    <Input
                      placeholder="Nom de l'entreprise"
                      prefix={<UserOutlined />}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "L'email est requis" },
                      { type: "email", message: "Email invalide" },
                    ]}
                  >
                    <Input
                      placeholder="exemple@domaine.com"
                      prefix={<MailOutlined />}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label="Adresse" name="address">
                    <Input.TextArea
                      rows={3}
                      placeholder="Adresse"
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Nom court" name="shortName">
                    <Input
                      placeholder="Nom court"
                      prefix={<UserOutlined />}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Téléphone"
                    name="phone"
                    rules={[
                      { required: true, message: "Le téléphone est requis" },
                    ]}
                  >
                    <Input
                      placeholder="Téléphone"
                      prefix={<PhoneOutlined />}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label="Site Web" name="website">
                    <Input
                      placeholder="https://www.example.com"
                      prefix={<GlobalOutlined />}
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Devise" name="currency">
                    <Select
                      size="large"
                      options={currencyOptions}
                      loading={currenciesLoading}
                      disabled={Boolean(selectedCompany) && currenciesLoading}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Profils d'activite"
                    name="activityProfiles"
                    extra={
                      isSuperAdmin
                        ? "Un ou plusieurs profils du MEME domaine. Active les fonctionnalites associees et suggere les packs d'initialisation."
                        : "L'attribution des profils d'activité est réservée au SysAdmin."
                    }
                  >
                    <Select
                      size="large"
                      mode="multiple"
                      allowClear
                      disabled={!isSuperAdmin}
                      placeholder="Selectionnez un ou plusieurs profils du meme domaine"
                      options={activityProfileOptions}
                      onChange={(selected) => {
                        // Garde-fou cumul INTRA-domaine : on ne conserve que les
                        // profils du domaine du 1er choisi, et on alerte l'utilisateur.
                        const domain = getSelectionDomain(selected);
                        const filtered = domain
                          ? selected.filter((value) => {
                              const option = activityProfileOptions.find(
                                (o) => o.value === value
                              );
                              return !option || option.domain === domain;
                            })
                          : selected;
                        if (filtered.length !== selected.length) {
                          form.setFieldsValue({ activityProfiles: filtered });
                          notification.warning({
                            message: "Cumul intra-domaine",
                            description:
                              "Les profils cumules doivent appartenir au meme domaine d'activite.",
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Statut"
                    name="isActive"
                    valuePropName="checked"
                    extra={
                      isSuperAdmin
                        ? undefined
                        : "La mise à jour du statut est réservée au SysAdmin."
                    }
                  >
                    <Switch
                      checkedChildren="Actif"
                      unCheckedChildren="Inactif"
                      disabled={!isSuperAdmin}
                      style={{ width: 80 }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {getCompanyProfiles(selectedCompany).length > 0 ? (
                    <Form.Item label="Profils actuels">
                      <Space size={[4, 4]} wrap>
                        {getActivityProfileLabels(selectedCompany).map((label) => (
                          <Tag color="processing" key={label}>
                            {label}
                          </Tag>
                        ))}
                      </Space>
                    </Form.Item>
                  ) : null}
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Logos et images" key="2">
              <Row gutter={24}>
                <Col xs={12} sm={6}>
                  <Form.Item
                    label="Light Logo"
                    name="lightLogo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) =>
                      Array.isArray(e) ? e : e && e.fileList
                    }
                  >
                    <Upload
                      name="light_logo"
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Light Logo</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item
                    label="Dark Logo"
                    name="darkLogo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) =>
                      Array.isArray(e) ? e : e && e.fileList
                    }
                  >
                    <Upload
                      name="dark_logo"
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Dark Logo</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item
                    label="Small Light Logo"
                    name="smallLightLogo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) =>
                      Array.isArray(e) ? e : e && e.fileList
                    }
                  >
                    <Upload
                      name="small_light_logo"
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Small Light</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item
                    label="Small Dark Logo"
                    name="smallDarkLogo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) =>
                      Array.isArray(e) ? e : e && e.fileList
                    }
                  >
                    <Upload
                      name="small_dark_logo"
                      listType="picture-card"
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Small Dark</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Numérotation" key="invoice-numbering">
              {selectedCompany?.id ? <InvoiceNumberingSettings companyId={selectedCompany.id} active={modalVisible} />
                : <Alert type="info" showIcon message="La numérotation sera automatique à la création. Vous pourrez ensuite autoriser la saisie manuelle dans les paramètres de l'entreprise." />}
            </TabPane>
          </Tabs>
        </Form>
      </Modal>

      <Modal
        open={detailVisible}
        title={
          <div className="company-detail-modal-title">
            <Avatar
              size={42}
              icon={<BankOutlined />}
              src={detailCompany?.light_logo}
            />
            <div>
              <Title level={4}>Détails de l'entreprise</Title>
              <Text type="secondary">
                Informations générales et paramètres de l'entreprise
              </Text>
            </div>
          </div>
        }
        width={isSmallScreen ? "96%" : 1120}
        centered
        destroyOnClose
        className="company-detail-modal"
        onCancel={() => setDetailVisible(false)}
        afterClose={() => {
          setDetailCompany(null);
          setDetailLoading(false);
        }}
        footer={[
          <Button
            key="close"
            className="company-detail-close-button"
            onClick={() => setDetailVisible(false)}
          >
            Fermer
          </Button>,
          ...(canEditCompanySettings && detailCompany
            ? [
                <Button
                  key="edit"
                  type="primary"
                  icon={<EditOutlined />}
                  className="company-detail-edit-button"
                  onClick={() => {
                    openEditModal(detailCompany);
                    setDetailVisible(false);
                  }}
                >
                  Modifier
                </Button>,
              ]
            : []),
        ]}
      >
        {detailLoading || !detailCompany ? (
          <div className="company-detail-loading">
            <Spin size="large" tip="Chargement des informations..." />
          </div>
        ) : (
          <div className="company-details">
            <Card bordered={false} className="company-detail-hero">
              <div className="company-detail-hero-content">
                <div className="company-detail-hero-main">
                  <Avatar
                    size={88}
                    icon={<BankOutlined />}
                    src={detailCompany.light_logo}
                    className="company-detail-logo"
                  />
                  <div className="company-detail-identity">
                    <Title level={3}>{detailCompany.name}</Title>
                    <Text type="secondary">
                      {displayValue(detailCompany.short_name, "Aucun nom court")}
                    </Text>
                    <Space size={[8, 8]} wrap>
                      <Tag color={detailCompany.status === "active" ? "success" : "error"}>
                        {detailCompany.status === "active" ? "Actif" : "Inactif"}
                      </Tag>
                      {isEnabled(detailCompany.verified) ? (
                        <Tag color="cyan" icon={<SafetyCertificateOutlined />}>
                          Vérifiée
                        </Tag>
                      ) : null}
                    </Space>
                  </div>
                </div>
                <div className="company-detail-metadata" aria-label="Informations clés">
                  <div className="company-detail-metadata-chip">
                    <AppstoreOutlined />
                    <span>
                      {displayValue(
                        detailCompany.business_domain ||
                          getSelectionDomain(getCompanyProfiles(detailCompany))
                      )}
                    </span>
                  </div>
                  <div className="company-detail-metadata-chip">
                    <NumberOutlined />
                    <span>{getCompanyCurrencyValue(detailCompany)}</span>
                  </div>
                </div>
              </div>
            </Card>

            <Tabs defaultActiveKey="information" className="company-detail-tabs">
              <TabPane tab="Informations de l'entreprise" key="information">
                <Row gutter={[16, 16]} className="company-detail-card-grid">
                  <Col span={24}>
                    <Card
                      className="company-detail-card company-detail-overview-card"
                      title={
                        <Space>
                          <IdcardOutlined />
                          Identité et statut
                        </Space>
                      }
                    >
                      <Descriptions
                        colon={false}
                        layout="vertical"
                        className="company-detail-overview"
                        column={{ xxl: 4, xl: 4, lg: 4, md: 2, sm: 1, xs: 1 }}
                      >
                        <Descriptions.Item label="Raison sociale">
                          {displayValue(detailCompany.name)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Nom court">
                          {displayValue(detailCompany.short_name)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Statut">
                          <Tag color={detailCompany.status === "active" ? "success" : "error"}>
                            {detailCompany.status === "active" ? "Actif" : "Inactif"}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Compte vérifié">
                          {isEnabled(detailCompany.verified) ? "Oui" : "Non"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Entreprise globale">
                          {isEnabled(detailCompany.is_global) ? "Oui" : "Non"}
                        </Descriptions.Item>
                        <Descriptions.Item
                          label="Profils d'activité"
                          span={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                        >
                          {getActivityProfileLabels(detailCompany).length ? (
                            <Space size={[4, 4]} wrap>
                              {getActivityProfileLabels(detailCompany).map((label) => (
                                <Tag color="processing" key={label}>
                                  {label}
                                </Tag>
                              ))}
                            </Space>
                          ) : (
                            "Non renseigné"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Domaine métier">
                          {displayValue(
                            detailCompany.business_domain ||
                              getSelectionDomain(getCompanyProfiles(detailCompany))
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card
                      title={
                        <Space>
                          <EnvironmentOutlined />
                          Coordonnées
                        </Space>
                      }
                      className="company-detail-card company-detail-section-card"
                    >
                      <Descriptions
                        colon={false}
                        column={1}
                        className="company-detail-list"
                      >
                        <Descriptions.Item label="Email">
                          {detailCompany.email ? (
                            <Text copyable>{detailCompany.email}</Text>
                          ) : (
                            "Non renseigné"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Téléphone">
                          {detailCompany.phone ? (
                            <Text copyable>{detailCompany.phone}</Text>
                          ) : (
                            "Non renseigné"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Site web">
                          {detailCompany.website ? (
                            <a
                              href={
                                /^https?:\/\//i.test(detailCompany.website)
                                  ? detailCompany.website
                                  : `https://${detailCompany.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {detailCompany.website}
                            </a>
                          ) : (
                            "Non renseigné"
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Adresse">
                          <div className="company-detail-multiline">
                            {displayValue(detailCompany.address)}
                          </div>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card
                      title={
                        <Space>
                          <NumberOutlined />
                          Activité et finances
                        </Space>
                      }
                      className="company-detail-card company-detail-section-card"
                    >
                      <Descriptions
                        colon={false}
                        column={1}
                        className="company-detail-list"
                      >
                        <Descriptions.Item label="Devise">
                          {detailCompany.currency_name
                            ? `${detailCompany.currency_name} (${getCompanyCurrencyValue(
                                detailCompany
                              )})${
                                detailCompany.currency_symbol
                                  ? ` · ${detailCompany.currency_symbol}`
                                  : ""
                              }`
                            : getCompanyCurrencyValue(detailCompany)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Préfixe de facturation">
                          {displayValue(detailCompany.prefixe_inv)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Établissement principal">
                          {detailCompany.warehouse_id
                            ? `#${detailCompany.warehouse_id}`
                            : "Non renseigné"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Clients en ligne">
                          {isEnabled(detailCompany.online_customer) ? "Activés" : "Désactivés"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card
                      title={
                        <Space>
                          <CalendarOutlined />
                          Abonnement et licence
                        </Space>
                      }
                      className="company-detail-card company-detail-section-card"
                    >
                      <Descriptions
                        colon={false}
                        column={1}
                        className="company-detail-list"
                      >
                        <Descriptions.Item label="Plan d'abonnement">
                          {detailCompany.subscription_plan_id
                            ? `#${detailCompany.subscription_plan_id}`
                            : "Non renseigné"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Périodicité">
                          {detailCompany.package_type === "annual"
                            ? "Annuelle"
                            : detailCompany.package_type === "semiannual"
                            ? "Semestrielle"
                            : detailCompany.package_type === "quarterly"
                            ? "Trimestrielle"
                            : detailCompany.package_type === "monthly"
                            ? "Mensuelle"
                            : displayValue(detailCompany.package_type)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Fin de la période d'essai">
                          {formatDate(detailCompany.trial_ends_at, true)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Expiration de la licence">
                          {formatDate(detailCompany.licence_expire_on)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Transaction de paiement">
                          {displayValue(detailCompany.payment_transcation_id)}
                        </Descriptions.Item>
                        {(detailCompany.card_brand || detailCompany.card_last_four) && (
                          <Descriptions.Item label="Carte enregistrée">
                            {`${displayValue(detailCompany.card_brand, "Carte")} •••• ${displayValue(
                              detailCompany.card_last_four,
                              "----"
                            )}`}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card
                      title={
                        <Space>
                          <SettingOutlined />
                          Configuration de l'application
                        </Space>
                      }
                      className="company-detail-card company-detail-section-card"
                    >
                      <Descriptions
                        colon={false}
                        column={1}
                        className="company-detail-list"
                      >
                        <Descriptions.Item label="Disposition">
                          {displayValue(detailCompany.app_layout)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Thème de la barre latérale">
                          {displayValue(detailCompany.left_sidebar_theme)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Couleur principale">
                          <Space>
                            {detailCompany.primary_color ? (
                              <span
                                className="company-color-swatch"
                                style={{ backgroundColor: detailCompany.primary_color }}
                              />
                            ) : null}
                            {displayValue(detailCompany.primary_color)}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Sens de lecture RTL">
                          {isEnabled(detailCompany.rtl) ? "Oui" : "Non"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Fuseau horaire">
                          {displayValue(detailCompany.timezone)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Détection automatique du fuseau">
                          {isEnabled(detailCompany.auto_detect_timezone) ? "Oui" : "Non"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Format de date">
                          {displayValue(detailCompany.date_format)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Format d'heure">
                          {displayValue(detailCompany.time_format)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Menus rapides">
                          {displayValue(detailCompany.shortcut_menus)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Langue application / site">
                          {`${displayValue(detailCompany.lang_id, "—")} / ${displayValue(
                            detailCompany.website_lang_id,
                            "—"
                          )}`}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col span={24}>
                    <Card
                      className="company-detail-card"
                      title={
                        <Space>
                          <PictureOutlined />
                          Identité visuelle
                        </Space>
                      }
                    >
                      {getCompanyLogoItems(detailCompany).some((item) => item.url) ? (
                        <div className="company-assets-grid">
                          {getCompanyLogoItems(detailCompany)
                            .filter((item) => item.url)
                            .map((item) => (
                              <div className="company-asset" key={item.key}>
                                <Text strong>{item.label}</Text>
                                <div
                                  className={`company-asset-preview${
                                    item.dark ? " company-asset-preview--dark" : ""
                                  }`}
                                >
                                  <img src={item.url} alt={item.label} />
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Aucun élément visuel enregistré"
                        />
                      )}
                    </Card>
                  </Col>

                  {detailCompany.customer_notes ? (
                    <Col span={24}>
                      <Card className="company-detail-card" title="Notes client">
                        <div className="company-detail-multiline">
                          {detailCompany.customer_notes}
                        </div>
                      </Card>
                    </Col>
                  ) : null}

                  <Col span={24}>
                    <Card className="company-detail-card" title="Données système">
                      <Descriptions
                        colon={false}
                        layout="vertical"
                        className="company-detail-overview"
                        column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
                      >
                        <Descriptions.Item label="Nombre d'utilisateurs">
                          {displayValue(detailCompany.total_users, 0)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Administrateur propriétaire">
                          {detailCompany.admin_id ? `#${detailCompany.admin_id}` : "Non renseigné"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Marque blanche configurée">
                          {isEnabled(detailCompany.white_label_completed) ? "Oui" : "Non"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Notifications de mise à jour">
                          {isEnabled(detailCompany.update_app_notification) ? "Activées" : "Désactivées"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mode débogage">
                          {isEnabled(detailCompany.app_debug) ? "Activé" : "Désactivé"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Créée le">
                          {formatDate(detailCompany.created_at, true)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Dernière modification">
                          {formatDate(detailCompany.updated_at, true)}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <BankOutlined />
                    Législations fiscales
                  </span>
                }
                key="legislation"
              >
                <CompanyLegislationManager companyId={detailCompany.id} />
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GestionCompanies;
